import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Pencil, Archive, Trash2, ArchiveRestore, Loader2, Search, X, Layers,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  refreshSocialMediaOptions,
} from '../../utils/socialMediaOptions';
import {
  AVAILABLE_PLATFORM_ICONS,
  renderIconByKey,
} from '../../utils/platformIcons';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── List type registry — keep in sync with backend EDITABLE_LIST_TYPES ─────
const LIST_TYPES = [
  { key: 'accounts', label: 'Accounts', singular: 'Account', description: 'Brand accounts / handles used on social media.', supportsColor: true },
  { key: 'accounts_with_college_expansion', label: 'College-Expansion Accounts', singular: 'College-Expansion Account', description: 'Subset of accounts that expand into per-college sub-rows in the tracker.', supportsColor: false },
  { key: 'colleges', label: 'Colleges', singular: 'College', description: 'Colleges surfaced in the college-expansion tracker.', supportsColor: true },
  { key: 'content_types', label: 'Content Types', singular: 'Content Type', description: 'Post formats — Video, Poster, Carousel, etc.', supportsColor: true },
  { key: 'languages', label: 'Languages', singular: 'Language', description: 'Supported content languages for posts.', supportsColor: false },
  { key: 'statuses', label: 'Statuses', singular: 'Status', description: 'Workflow statuses posts move through.', supportsColor: true },
  { key: 'platforms', label: 'Platforms', singular: 'Platform', description: 'Social platforms — Instagram, LinkedIn, YouTube, etc. Icon rendering falls back for names not in the icon library.', supportsColor: false },
];

const emptyForm = {
  name: '',
  description: '',
  color: '',
  icon: '',
  is_active: true,
};

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const SocialMediaOptions = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(LIST_TYPES[0].key);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = create, object = edit
  const [form, setForm] = useState(emptyForm);

  const active = LIST_TYPES.find((t) => t.key === activeTab) || LIST_TYPES[0];
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const loadItems = useCallback(async () => {
    if (!token) return;
    const requested = activeTab;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/social-media/admin/options/${requested}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Guard against a stale, slower response from a previously-active tab
      // landing after the user has already switched tabs.
      if (requested !== activeTabRef.current) return;
      setItems(res.data.items || []);
    } catch (e) {
      if (requested !== activeTabRef.current) return;
      toast.error(e.response?.data?.detail || `Failed to load ${requested}`);
    } finally {
      if (requested === activeTabRef.current) setLoading(false);
    }
  }, [token, activeTab]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      description: item.description || '',
      color: item.color || '',
      icon: item.icon || '',
      is_active: item.is_active !== false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error('Name is required');
      return;
    }
    const trimmedColor = form.color.trim();
    if (trimmedColor && !HEX_COLOR_REGEX.test(trimmedColor)) {
      toast.error('Color must be a valid hex value, e.g. #3B82F6');
      return;
    }
    setSaving(true);
    const payload = {
      name,
      description: form.description.trim() || null,
      color: trimmedColor || null,
      icon: form.icon.trim() || null,
      is_active: form.is_active,
    };
    try {
      if (editingItem) {
        await axios.put(
          `${API}/social-media/admin/options/${activeTab}/${editingItem.id}`,
          payload,
          authHeader,
        );
        toast.success(`Updated "${name}"`);
      } else {
        await axios.post(`${API}/social-media/admin/options/${activeTab}`, payload, authHeader);
        toast.success(`Created "${name}"`);
      }
      setShowModal(false);
      await loadItems();
      await refreshSocialMediaOptions(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(
        `${API}/social-media/admin/options/${activeTab}/${item.id}`,
        { is_active: !item.is_active },
        authHeader,
      );
      toast.success(item.is_active ? `Archived "${item.name}"` : `Activated "${item.name}"`);
      await loadItems();
      await refreshSocialMediaOptions(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}" permanently? Historical posts using this value are unaffected but new dropdowns will no longer show it.`)) return;
    try {
      await axios.delete(`${API}/social-media/admin/options/${activeTab}/${item.id}`, authHeader);
      toast.success(`Deleted "${item.name}"`);
      await loadItems();
      await refreshSocialMediaOptions(token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const filtered = items.filter((i) =>
    i.name.toLowerCase().includes(search.toLowerCase()),
  );

  const stats = {
    total: items.length,
    active: items.filter((i) => i.is_active).length,
    archived: items.filter((i) => !i.is_active).length,
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="sm-options-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-brand-navy text-white flex items-center justify-center flex-shrink-0">
            <Layers size={20} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Social Media Options</h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage the dropdown values (accounts, content types, languages, statuses, …) used across the Social Media module. Changes propagate instantly across every screen.
            </p>
          </div>
        </div>
        <Button
          onClick={openCreate}
          className="bg-brand-navy hover:bg-brand-navy-hover text-white"
          data-testid="sm-options-add-btn"
        >
          <Plus size={16} className="mr-2" />
          Add {active.singular}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200">
        {LIST_TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => { setActiveTab(t.key); setSearch(''); }}
            data-testid={`sm-options-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.key
                ? 'border-brand-accent text-brand-navy'
                : 'border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Description */}
      <p className="text-sm text-slate-600 mb-4">{active.description}</p>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Total</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="sm-options-stat-total">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Active</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{stats.active}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Archived</p>
          <p className="text-2xl font-bold text-slate-400 mt-1">{stats.archived}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg p-4 border border-slate-200 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={`Search ${active.label.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="sm-options-search"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-slate-500">
            <Loader2 className="animate-spin mr-2" size={18} />
            Loading {active.label.toLowerCase()}...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="sm-options-empty">
            No {active.label.toLowerCase()} found
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide w-20">Order</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Description</th>
                {active.supportsColor && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Color</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id} data-testid={`sm-options-row-${item.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm text-slate-500 font-mono">{item.order}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      {active.key === 'platforms' && (
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0" title={item.icon || 'auto'}>
                          {renderIconByKey(item.icon, 16)}
                        </span>
                      )}
                      {item.color && (
                        <span
                          className="w-3 h-3 rounded-full border border-slate-300 flex-shrink-0"
                          style={{ backgroundColor: item.color }}
                          title={item.color}
                        />
                      )}
                      <span className="font-medium text-slate-900">{item.name}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-md truncate">
                    {item.description || <span className="text-slate-300">—</span>}
                  </td>
                  {active.supportsColor && (
                    <td className="px-4 py-3 text-xs text-slate-600 font-mono">
                      {item.color || <span className="text-slate-300">—</span>}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    {item.is_active ? (
                      <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-1 rounded">Active</span>
                    ) : (
                      <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-1 rounded">Archived</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(item)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                        title="Edit"
                        data-testid={`sm-options-edit-${item.id}`}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => toggleActive(item)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-slate-900"
                        title={item.is_active ? 'Archive' : 'Activate'}
                        data-testid={`sm-options-toggle-${item.id}`}
                      >
                        {item.is_active ? <Archive size={15} /> : <ArchiveRestore size={15} />}
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-500 hover:text-red-600"
                        title="Delete"
                        data-testid={`sm-options-delete-${item.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem ? `Edit ${active.singular}` : `Add ${active.singular}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="sm-option-name">Name<span className="text-red-500 ml-0.5">*</span></Label>
              <Input
                id="sm-option-name"
                autoFocus
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={80}
                placeholder={`e.g. ${active.key === 'statuses' ? 'In Review' : 'New value'}`}
                data-testid="sm-option-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sm-option-desc">Description</Label>
              <Textarea
                id="sm-option-desc"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional context or usage note"
                rows={2}
                data-testid="sm-option-desc-input"
              />
            </div>
            {active.key === 'platforms' && (
              <div className="space-y-1.5">
                <Label htmlFor="sm-option-icon">Platform icon</Label>
                <div className="flex items-center gap-2">
                  <select
                    id="sm-option-icon"
                    value={form.icon || ''}
                    onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                    className="flex-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                    data-testid="sm-option-icon-select"
                  >
                    <option value="">— Auto (match by name, else generic) —</option>
                    {AVAILABLE_PLATFORM_ICONS.map((o) => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                  {form.icon && (
                    <span className="w-10 h-10 rounded border border-slate-200 bg-white flex items-center justify-center flex-shrink-0">
                      {renderIconByKey(form.icon, 20)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500">Rendered next to the platform name everywhere posts appear (Posts table, Calendar, Reports, filters, etc.). Leave blank for automatic best-match.</p>
              </div>
            )}
            {active.supportsColor && (
              <div className="space-y-1.5">
                <Label htmlFor="sm-option-color">Color (hex)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="sm-option-color"
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    placeholder="#338AF1"
                    className="font-mono"
                    maxLength={9}
                    data-testid="sm-option-color-input"
                  />
                  {form.color && (
                    <span
                      className="w-9 h-9 rounded border border-slate-300 flex-shrink-0"
                      style={{ backgroundColor: form.color }}
                    />
                  )}
                </div>
                <p className="text-xs text-slate-500">Used to colour-code {active.label.toLowerCase()} badges across the Social Media UI.</p>
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <input
                id="sm-option-active"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="rounded"
                data-testid="sm-option-active-input"
              />
              <Label htmlFor="sm-option-active" className="font-normal cursor-pointer select-none">
                Active — show in dropdowns
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-brand-navy hover:bg-brand-navy-hover text-white"
              data-testid="sm-option-save-btn"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : editingItem ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialMediaOptions;
